import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  CheckCircle,
  Layout,
  Eye,
  Monitor,
  Palette,
  Layers,
  Zap,
  Star,
  Target,
} from "lucide-react";
import PortfolioNavbar from "./PortfolioNavbar";
import PageHero from "./PageHero";
import PortfolioFooter from "./PortfolioFooter";

const services = [
  {
    icon: <Eye className="w-7 h-7 text-purple-500" />,
    title: "UX Research & Strategy",
    desc: "We dig into user behaviour, pain points, and mental models to craft experiences that feel intuitive — reducing churn and increasing satisfaction.",
    features: [
      "User interviews & surveys",
      "Persona & journey mapping",
      "Competitive UX benchmarking",
      "Information architecture design",
    ],
  },
  {
    icon: <Layout className="w-7 h-7 text-purple-500" />,
    title: "UI Design & Prototyping",
    desc: "Pixel-perfect interfaces built in Figma with detailed component libraries, interactive prototypes, and developer-ready design tokens.",
    features: [
      "Wireframes & interactive prototypes",
      "Design system & component library",
      "Responsive & mobile-first layouts",
      "Accessibility (WCAG 2.1) compliance",
    ],
  },
  {
    icon: <Palette className="w-7 h-7 text-purple-500" />,
    title: "Brand Identity Design",
    desc: "A cohesive brand identity that resonates — logo design, colour systems, typography, brand guidelines, and all the assets you need to show up confidently.",
    features: [
      "Logo & visual identity creation",
      "Brand colour & typography system",
      "Brand guidelines document",
      "Stationery & marketing collateral",
    ],
  },
  {
    icon: <Monitor className="w-7 h-7 text-purple-500" />,
    title: "Design-to-Development Handoff",
    desc: "We bridge the gap between design and engineering with clean Figma specs, developer notes, and active collaboration during implementation.",
    features: [
      "Figma Dev Mode annotations",
      "Design token documentation",
      "Front-end QA & review support",
      "Design system governance",
    ],
  },
];

const tools = [
  { name: "Figma", color: "from-purple-500 to-pink-500" },
  { name: "Adobe XD", color: "from-pink-600 to-purple-600" },
  { name: "Illustrator", color: "from-orange-500 to-yellow-500" },
  { name: "Photoshop", color: "from-blue-600 to-blue-800" },
  { name: "Framer", color: "from-gray-700 to-gray-900" },
  { name: "After Effects", color: "from-purple-700 to-indigo-800" },
];

const stats = [
  { value: "35%", label: "Avg Conversion Uplift", icon: <Target className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "200+", label: "Screens Designed", icon: <Layers className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "50+", label: "Brand Identities", icon: <Star className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
  { value: "4.9★", label: "Client Design Rating", icon: <Zap className="w-6 h-6 text-pink-300 mx-auto mb-2" /> },
];

const whyUs = [
  "Senior designers with 5+ years SaaS experience",
  "User-testing at every stage of design",
  "Unlimited revisions until you love it",
  "Figma source files handed over on completion",
  "Design system maintained post-launch",
  "Integrated with your engineering team",
];

const UIUXBranding = () => {
  const navigate = useNavigate();

  return (
    <div className="font-sans">
      <Helmet>
        <title>UI/UX & Brand Identity | Winsil Evolution</title>
        <meta name="description" content="User-centric UI/UX design and brand identity services from Winsil Evolution. Figma prototypes, design systems, logo design and brand guidelines." />
      </Helmet>

      <PortfolioNavbar />

      <PageHero
        label="Branding — UI/UX & Identity"
        labelClass="font-black bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"
        title="UI/UX & Brand Identity"
        subtitle="Beautiful, intuitive designs and cohesive brand identities that convert visitors into customers and customers into advocates."
        image="/images/photo-1561070791-2526d30994b5.jpg"
        bgClass="bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900"
        glowColor="#d946ef"
        titleClass="text-white"
      />

      {/* ── Image Gallery ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-center text-purple-500 mb-2">Design Portfolio</p>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">Designs That Leave an Impression</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { src: "/images/photo-1586717791821-3f44a563fa4c.jpg", alt: "UI design on screen", label: "UI Design" },
              { src: "/images/photo-1626785774573-4b799315345d.jpg", alt: "Brand identity design", label: "Brand Identity" },
              { src: "/images/photo-1609921212029-bb5a28e60960.jpg", alt: "Design process and wireframing", label: "Wireframing" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-64">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {[
              { src: "/images/photo-1561070791-2526d30994b5.jpg", alt: "Design prototyping with Figma", label: "Prototyping" },
              { src: "/images/photo-1581291518857-4e27b48ff24e.jpg", alt: "UX research and user testing", label: "UX Research" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-56">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-pink-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Services Grid ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-purple-500 text-center mb-2">What We Offer</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-14">
            Our Design Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((s, i) => (
              <div
                key={i}
                className="group bg-gray-50 border border-gray-100 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-purple-50 border border-purple-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-purple-100 transition-colors duration-300">
                  {s.icon}
                </div>
                <h3 className="text-xl font-extrabold text-gray-900 mb-3">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">{s.desc}</p>
                <ul className="space-y-2">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-center space-x-2">
                      <CheckCircle className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Design Tools ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-purple-50">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-purple-500 mb-2">Our Toolkit</p>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-10">Design Tools We Master</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {tools.map((t) => (
              <div
                key={t.name}
                className={`bg-gradient-to-br ${t.color} text-white px-7 py-3 rounded-full text-sm font-black shadow-md hover:scale-105 transition-transform duration-200 cursor-default`}
              >
                {t.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-purple-800">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
          {stats.map((s) => (
            <div key={s.label}>
              {s.icon}
              <p className="text-4xl sm:text-5xl font-black">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/50 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Why Us ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.25em] text-purple-500 mb-3">Why Winsil</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
                Design That{" "}
                <span className="text-purple-600">Converts</span> &amp; Delights
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Great design isn't just about looking good — it's about guiding
                users to take action. We blend aesthetics with psychology to
                create interfaces that are beautiful, usable, and profitable.
              </p>
              <ul className="space-y-3 mb-10">
                {whyUs.map((item) => (
                  <li key={item} className="flex items-center space-x-3">
                    <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                    <span className="text-gray-700 text-sm font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/contact")}
                className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-purple-700 text-white hover:bg-purple-600 transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Start a Design Project
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="/images/photo-1581291518857-4e27b48ff24e.jpg"
                alt="UI UX design process"
                className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/60 via-transparent to-orange-500/10" />
              <div className="absolute bottom-5 right-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
                <p className="text-2xl font-black text-purple-800">4.9★</p>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Client Rating</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA Banner ── */}
      <div className="relative bg-purple-900 py-16 px-4 overflow-hidden text-white text-center">
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }}
        />
        <div className="relative z-10 max-w-xl mx-auto">
          <p className="text-[11px] font-black uppercase tracking-[0.25em] text-pink-300 mb-3">Let's Create</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-5">Ready to Transform Your Brand?</h2>
          <p className="text-white/55 mb-8">Share your vision and we'll deliver a design concept in 5 business days.</p>
          <button
            onClick={() => navigate("/contact")}
            className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30"
          >
            Start My Design Project
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default UIUXBranding;
